import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success('If this email is registered, a reset link has been sent.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="forgot-password-page">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-forest mb-2" data-testid="forgot-password-heading">
            Reset Password
          </h1>
          <p className="text-muted-foreground">
            {sent ? 'Check your email for the reset link' : 'Enter your email to receive a password reset link'}
          </p>
        </div>

        {!sent ? (
          <form onSubmit={handleSubmit} className="mt-8 space-y-6" data-testid="forgot-password-form">
            <div>
              <label htmlFor="email" className="text-sm uppercase tracking-[0.1em] text-forest">Email</label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="mt-2 w-full px-4 py-3 bg-neutral-warm border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
                data-testid="forgot-email-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-forest text-ivory px-8 py-4 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em] disabled:opacity-50"
              data-testid="forgot-submit-button"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        ) : (
          <div className="bg-neutral-warm p-8 rounded-sm text-center space-y-4" data-testid="forgot-success-message">
            <div className="w-16 h-16 mx-auto rounded-full bg-forest/10 flex items-center justify-center">
              <Mail className="w-8 h-8 text-forest" />
            </div>
            <p className="text-forest leading-relaxed">
              We've sent a password reset link to <strong>{email}</strong>. Please check your inbox and click the link to reset your password.
            </p>
            <p className="text-xs text-muted-foreground">
              The link will expire in 1 hour. If you don't see the email, check your spam folder.
            </p>
          </div>
        )}

        <div className="text-center">
          <Link
            to="/login"
            className="inline-flex items-center text-sm text-forest hover:text-gold transition-colors"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
